import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Divider, Grid2, Paper, Stack, Typography } from '@mui/material';
import { FieldValues, useForm } from 'react-hook-form';
import { AddressInput, FormField, FormFieldType, API_FIELDS_CONTACT as CONTACT } from '@enroller/admissions-components';
import { useHeaderContext, ValidationType } from '../../../contexts/HeaderContext';
import { usePageTitle } from '@enroller/utils';
import { getConfig } from '../../../utils/config';
import { stateOptions } from '../../../constants';
import { defaultValues, InterviewFormValues, interviewSchema } from './InterviewFormValues';
import { zodResolver } from '@hookform/resolvers/zod';
import { useApplicationContext } from '../../../contexts/ApplicationContext/ApplicationContext';

const MAX_CHARACTERS_DESCRIPTION = 1000;

const interviewReportAcceptedMimeTypes = {
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'application/pdf': ['.pdf'],
};

const requestedHFAddressInputs: AddressInput[] = [
  { name: CONTACT.addressLine1, label: 'Requested Host Family Address Line 1', required: true },
  { name: CONTACT.city, label: 'Requested Host Family City', required: true },
  { name: CONTACT.postalCode, label: 'Requested Host Family Zip', required: true },
  { name: CONTACT.addressLine2, label: 'Requested Host Family Address Line 2' },
  { name: CONTACT.state, label: 'Requested Host Family State', required: true },
];

const requestedSchoolAddressInputs: AddressInput[] = [
  { name: CONTACT.addressLine1, label: 'Requested School Address Line 1', required: true },
  { name: CONTACT.city, label: 'Requested School City', required: true },
  { name: CONTACT.postalCode, label: 'Requested School Zip', required: true },
  { name: CONTACT.addressLine2, label: 'Requested School Address Line 2' },
  { name: CONTACT.state, label: 'Requested School State', required: true },
];

const gradeLevelOptions = [
  {
    value: '8th',
  },
  {
    value: '9th',
  },
  {
    value: '10th',
  },
  {
    value: '11th',
  },
  {
    value: '12th',
  },
];

const recommendedGradeOptions = [
  {
    value: '9th',
  },
  {
    value: '10th',
  },
  {
    value: '11th',
  },
  {
    value: '12th',
  },
];

const gpaOptions = [
  {
    value: 'A',
  },
  {
    value: 'A-',
  },
  {
    value: 'B+',
  },
  {
    value: 'B',
  },
  {
    value: 'B-',
  },
  {
    value: 'C+',
  },
  {
    value: 'C',
  },
  {
    value: 'C-',
  },
  {
    value: 'D+',
  },
  {
    value: 'D',
  },
  {
    value: 'D-',
  },
];

const requestsOptions = [
  { value: 'Warm state request (CA, AZ, NM, TX, FL, GA, SC)' },
  { value: 'Urban/suburban' },
  { value: 'Mountain state request (CO, MT, ID, WY, UT, WA, OR)' },
];

const academicInterestDegreeOptions = [{ value: 'Great' }, { value: 'Average' }, { value: 'Little' }];

const personalityRangesOptions = [
  { name: 'opinionatedOpenMinded', leftLabel: 'Opinionated', rightLabel: 'Open-Minded' },
  { name: 'nervousCalm', leftLabel: 'Nervous', rightLabel: 'Calm' },
  { name: 'shyAssertive', leftLabel: 'Shy', rightLabel: 'Assertive' },
  { name: 'introspectiveOutgoing', leftLabel: 'Introspective', rightLabel: 'Outgoing' },
  { name: 'rigidFlexible', leftLabel: 'Rigid', rightLabel: 'Flexible' },
  { name: 'protectedIndependent', leftLabel: 'Protected', rightLabel: 'Independent' },
  { name: 'passiveTalkative', leftLabel: 'Passive', rightLabel: 'Talkative' },
];

const englishLevelOptions = [
  {
    value: '10. Absolute proficiency in English.',
    label: '10. Absolute proficiency in English. Student is able to both understand and converse. Student thinks in English.',
  },
  { value: '9. Near fluency.', label: '9. Near fluency. Can understand and respond to difficult questions. Has no problems communicating in English.' },
  {
    value: '8. English responses, although not perfect, come easily.',
    label: '8. English responses, although not perfect, come easily. Can respond intelligently, but needs practice.',
  },
  {
    value: '7. Speaking ability is good, but needs practice.',
    label: '7. Speaking ability is good, but needs practice. Student can go beyond basic responses. Needs to think before responding.',
  },
  {
    value: '6. Understands basic English.',
    label:
      '6. Understands basic English. Vocabulary deals with everyday, common terms. Thinks quickly, but it is evident that the student is translating. Can carry on a conversation.',
  },
  {
    value: '5. Understands much more than the student can communicate; however, makes an effort.',
    label: '5. Understands much more than the student can communicate; however, makes an effort. Can respond in some sentence forms, even if not perfect.',
  },
  {
    value: '4. Understands basic English sentences and is able to respond in words.',
    label: "4. Understands basic English sentences and is able to respond in words. Total immersion in English will improve the student's ability to respond.",
  },
  { value: '3. Understands words, but not sentences.', label: '3. Understands words, but not sentences. Speaking ability is limited to a few words.' },
  { value: '2. Has little or no ability to communicate.', label: '2. Has little or no ability to communicate. Student hesitates to use English.' },
  { value: '1. No understanding of English.', label: '1. No understanding of English.' },
];

const successPotentialOptions = [
  { value: 'Very Good Potential' },
  { value: 'Good Potential' },
  { value: 'Average Potential' },
  { value: 'Low Potential' },
  { value: 'No Potential' },
];

export interface InterviewFormProps {
  applicationData?: InterviewFormValues;
  submitApplication: (data: FieldValues) => void;
}

export const InterviewForm = ({ applicationData }: InterviewFormProps) => {
  const { googleMapsAPIKey } = getConfig();
  const { setSubmit, setHeaderTitle } = useHeaderContext();
  const { setPageTitle } = usePageTitle();
  const [validationMode, setValidationMode] = useState<ValidationType | ''>('');
  const { setApplicationPageData, triggerErrors, displayErrors } = useApplicationContext();

  useEffect(() => {
    setHeaderTitle({ title: 'Interview', subtitle: '' });
    setPageTitle('Interview');
  }, [setHeaderTitle, setPageTitle]);

  // Define resolver based on validation mode needed
  const resolver = useMemo(() => {
    const currentSchema = validationMode === ValidationType.Full ? interviewSchema.required : interviewSchema.optional;
    return zodResolver(currentSchema);
  }, [validationMode]);

  const {
    control,
    handleSubmit,
    reset,
    trigger,
    formState: { errors },
  } = useForm<InterviewFormValues>({
    defaultValues,
    resolver,
  });

  // Trigger errors if they have been triggered in another form
  useEffect(() => {
    if (displayErrors) setValidationMode(ValidationType.Full);
  }, [displayErrors, trigger]);

  useEffect(() => {
    if (applicationData) {
      reset({
        ...applicationData,
      });
    }
  }, [applicationData, reset]);

  const onSubmit = useCallback(
    () =>
      handleSubmit(
        (data) => {
          setApplicationPageData('interview', data);
          if (validationMode === ValidationType.Full) triggerErrors();
          console.log('Form submitted:', data);
        },
        () => triggerErrors(),
      )(),
    [handleSubmit, setApplicationPageData, triggerErrors, validationMode],
  );

  // Submit form when resolver is changed.
  useEffect(() => {
    if (validationMode) {
      onSubmit();
      setValidationMode('');
    }
  }, [resolver, validationMode, onSubmit]);

  useEffect(() => {
    setSubmit(setValidationMode);
  }, [setSubmit, setValidationMode]);

  return (
    <Stack component="form" gap={2}>
      <Paper sx={{ p: 2 }}>
        <Grid2 container spacing={4}>
          <Grid2 size={12}>
            <Typography variant="subtitle1">Interview</Typography>
          </Grid2>
          <Grid2 size={4}>
            <FormField
              type={FormFieldType.Number}
              label="Interview Length"
              name="interviewDetails.interviewLength"
              control={control}
              textSuffix="minutes"
              // Prevent error message and helper text showing up at the same time
              helperText={!errors.interviewDetails?.interviewLength ? 'Interview must be at least 45 minutes long' : ''}
            />
          </Grid2>
          <Grid2 size={4}>
            <FormField type={FormFieldType.Date} label="Date of Interview" name="interviewDetails.interviewDate" control={control} />
          </Grid2>
          <Grid2 size={4}>
            <FormField type={FormFieldType.Text} label="Name of Interviewer" name="interviewDetails.interviewerName" control={control} />
          </Grid2>
          <Grid2 size={6}>
            <FormField
              type={FormFieldType.Radio}
              label="Student grade level at time of interview"
              name="interviewDetails.gradeLevel"
              control={control}
              options={gradeLevelOptions}
            />
          </Grid2>
          <Grid2 size={6}>
            <FormField
              type={FormFieldType.Radio}
              label="Recommended Grade for Enrollment in the U.S."
              name="interviewDetails.recommendedGrade"
              control={control}
              options={recommendedGradeOptions}
            />
          </Grid2>
          <Grid2 size={12}>
            <FormField type={FormFieldType.Radio} label="GPA" name="interviewDetails.gpa" control={control} options={gpaOptions} />
          </Grid2>
        </Grid2>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Grid2 container spacing={2}>
          <Grid2 size={12}>
            <Typography variant="subtitle1">Special Requests</Typography>
          </Grid2>
          <Grid2 size={4}>
            <FormField type={FormFieldType.Dropdown} label="State Choice 1" name="specialRequests.stateChoice1" options={stateOptions} control={control} />
          </Grid2>
          <Grid2 size={4}>
            <FormField type={FormFieldType.Dropdown} label="State Choice 2" name="specialRequests.stateChoice2" options={stateOptions} control={control} />
          </Grid2>
          <Grid2 size={4}>
            <FormField type={FormFieldType.Dropdown} label="State Choice 3" name="specialRequests.stateChoice3" options={stateOptions} control={control} />
          </Grid2>
          <Grid2 size={12}>
            <Divider sx={{ m: 0 }} />
          </Grid2>
          <Grid2 size={12}>
            <FormField control={control} name="specialRequests.requests" type={FormFieldType.Checkboxset} options={requestsOptions} label="" />
          </Grid2>
        </Grid2>
      </Paper>
      <Paper sx={{ p: 2 }}>
        <Grid2 container spacing={2}>
          <Grid2 size={12}>
            <Typography variant="subtitle1">Direct Placement</Typography>
          </Grid2>
          <Grid2 size={12}>
            <FormField
              control={control}
              name="directPlacement.studentRequestingPlacement"
              type={FormFieldType.Checkbox}
              label="Is this student requesting a Direct Placement?"
            />
          </Grid2>
          <Grid2 size={4}>
            <FormField type={FormFieldType.Text} label="Requested Host Family Name" name="directPlacement.requestedHFName" control={control} />
          </Grid2>
          <Grid2 size={4}>
            <FormField type={FormFieldType.Text} label="Requested Host Family Phone" name="directPlacement.requestedHFPhone" control={control} />
          </Grid2>
          <Grid2 size={12}>
            <FormField
              type={FormFieldType.AddressAutocomplete}
              label="Requested Host Family Address"
              name="directPlacement.requestedHFAddress"
              control={control}
              googleMapsAPIKey={googleMapsAPIKey}
              addressInputs={requestedHFAddressInputs}
              restrictToUS
            />
          </Grid2>
          <Grid2 size={4}>
            <FormField type={FormFieldType.Text} label="Requested School Name" name="directPlacement.requestedSchoolName" control={control} />
          </Grid2>
          <Grid2 size={4}>
            <FormField type={FormFieldType.Text} label="Requested School Phone" name="directPlacement.requestedSchoolPhone" control={control} />
          </Grid2>
          <Grid2 size={12}>
            <FormField
              type={FormFieldType.AddressAutocomplete}
              label="Requested School Address"
              name="directPlacement.requestedSchoolAddress"
              control={control}
              googleMapsAPIKey={googleMapsAPIKey}
              addressInputs={requestedSchoolAddressInputs}
              restrictToUS
            />
          </Grid2>
        </Grid2>
      </Paper>
      <Paper sx={{ p: 2 }}>
        <Grid2 container spacing={2}>
          <Grid2 size={12}>
            <Typography variant="subtitle1">Student Evaluation</Typography>
          </Grid2>
          <Grid2 size={12}>
            <Alert variant="outlined" severity="info">
              Please describe the applicant's character and personality, such as his/her communication skills, emotional stability, curiosity, common sense,
              maturity and flexibility. Comment on the applicant's strengths and skills. Please also comment on what about this applicant will most likely
              appeal to a potential host family.
              <br />
              <br />
              IMPORTANT: The field below will be used for creating a short bio of the student to present to host families. This is your opportunity to explain
              what is special and/or unique about the student and what makes him/her stand out.
              <br />
              <br />
              Please provide sufficient detail. This section should be at least 6-8 sentences long.
            </Alert>
          </Grid2>
          <Grid2 size={12}>
            <FormField
              type={FormFieldType.Text}
              label="Student's Character"
              name="studentEvaluation.studentCharacter"
              control={control}
              multiline
              rows={6}
              maxLength={MAX_CHARACTERS_DESCRIPTION}
            />
          </Grid2>
          <Grid2 size={12}>
            <FormField
              type={FormFieldType.Text}
              label="Interviewer's Comments"
              name="studentEvaluation.interviewerComments"
              control={control}
              multiline
              rows={6}
              maxLength={MAX_CHARACTERS_DESCRIPTION}
            />
          </Grid2>
          <Grid2 size={6}>
            <FormField
              type={FormFieldType.Radio}
              label="Degree of academic interest"
              name="studentEvaluation.academicInterestDegree"
              control={control}
              options={academicInterestDegreeOptions}
            />
          </Grid2>
          <Grid2 size={12}>
            <Alert variant="outlined" severity="info">
              Please indicate on the scale below how close you believe this student comes to each pair of personality extremes.
            </Alert>
          </Grid2>
          <Grid2 size={12}>
            <FormField
              control={control}
              type={FormFieldType.SelectionRangeTable}
              name="studentEvaluation.personalityRanges"
              options={personalityRangesOptions}
            />
          </Grid2>
        </Grid2>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Grid2 container spacing={2}>
          <Grid2 size={12}>
            <Alert variant="outlined" severity="info">
              Please select the score (1 to 10) that best describes the student's ability to understand and speak English. Use the guidelines next to each score
              for your evaluation.
            </Alert>
          </Grid2>
          <Grid2 size={12}>
            <FormField
              type={FormFieldType.Radio}
              label="English Comprehension Score"
              name="englishScore.englishLevel"
              control={control}
              options={englishLevelOptions}
              direction="vertical"
            />
          </Grid2>
          <Grid2 size={12}>
            <FormField
              type={FormFieldType.Radio}
              label="How would you predict the potential success of this applicant as an exchange student in the United States?"
              name="englishScore.successPotential"
              control={control}
              options={successPotentialOptions}
            />
          </Grid2>
          <Grid2 size={12}>
            <Divider sx={{ m: 0 }} />
          </Grid2>
          <Grid2 size={12}>
            <FormField
              label="Interview Report Upload (optional)"
              id="interviewReport"
              control={control}
              name="englishScore.interviewReport"
              type={FormFieldType.FileUploader}
              acceptedMimeTypes={interviewReportAcceptedMimeTypes}
            />
          </Grid2>
        </Grid2>
      </Paper>
    </Stack>
  );
};
